/**
 * 聊天 SSE 长连接 Hook
 *
 * 替代原 useChatWebSocket，使用 EventSource/fetch 流式接收服务端消息。
 *
 * 接口：GET /sse/events
 * Header：Chat-Token、Chat-Session
 *
 * ★ 核心保证：全局永远只有 1 个活跃 SSE 连接（模块级单例 + generation 锁）
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useUserStore, userStoreRehydrated } from "@/store/useUserStore";
import { useChatSessionStore } from "@/store/useChatSessionStore";

type SseStatus = "idle" | "connecting" | "open" | "closed" | "error";

/** SSE 出站消息类型（保留兼容性） */
export type ChatSseOutbound =
  | { type: "ping" }
  | { type: "text"; content: string }
  | { type: "audio"; content: string }
  | Record<string, unknown>;

/** SSE 入站消息类型 */
export type ChatSseInbound =
  | {
      event: "connected";
      data: {
        identity: {
          is_guest: boolean;
          uid: number | null;
          session_id: string;
          token: string | null;
          new_session: boolean;
        };
        type: "guest" | "user";
        timestamp: number;
      };
    }
  | {
      event: string; // "text" | "audio" | "navigation" 等
      data: {
        id?: number;
        sender_id?: number;
        receiver_id?: number;
        content: string;
        msg_type?: "text" | "greeting" | "navigation" | string;
        source?: "user" | "bot";
        created_at?: number;
        read_at?: number;
        audio_path?: string;
        audio_duration?: number;
        /** 支持字符串URL 或 SSE 推送的 ["base64", 数据] 数组格式 */
        audio_url?: string | [string, string];
        nickname?: string;
        avatar?: string;
        session_id?: string;
        is_guest?: number;
      };
    }
  | Record<string, unknown>;

export interface UseChatSSEOptions {
  /** SSE 接口地址，例如 /sse/events 或完整 URL */
  url: string;
  /** 是否启用（默认 true） */
  enabled?: boolean;
  /** 重连最小延迟，毫秒（默认 500） */
  reconnectMinDelayMs?: number;
  /** 重连最大延迟，毫秒（默认 15000） */
  reconnectMaxDelayMs?: number;
  /** 收到消息回调 */
  onMessage?: (msg: ChatSseInbound) => void;
  /** 日志开关 */
  debug?: boolean;
}

// ════════════════════════════════════════════════════════
// ★ 模块级单例：确保全局最多 1 个活跃 SSE 连接
// ════════════════════════════════════════════════════════

let _globalGen = 0;           // 全局连接代数（每次 connect/close 递增）
let _activeAbortCtrl: AbortController | null = null; // 当前活跃的 AbortController

function _abortGlobal(): void {
  if (_activeAbortCtrl) {
    _activeAbortCtrl.abort();
    _activeAbortCtrl = null;
  }
}

/** 让当前及所有进行中的异步操作全部失效 */
function _invalidate(): void {
  _globalGen++;
  _abortGlobal();
}

export function useChatSSE(options: UseChatSSEOptions) {
  const {
    url,
    enabled = true,
    reconnectMinDelayMs = 500,
    reconnectMaxDelayMs = 15_000,
    onMessage,
    debug = false,
  } = options;

  const { token } = useUserStore();
  const { session_id } = useChatSessionStore();

  const [status, setStatus] = useState<SseStatus>("idle");
  const statusRef = useRef<SseStatus>("idle");
  const [lastMessage, setLastMessage] = useState<ChatSseInbound | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [connectedUid, setConnectedUid] = useState<number | null>(null);

  // 实例级 ref（不参与竞态控制，仅用于 UI 状态）
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closedByUserRef = useRef(false);

  const log = useCallback(
    (...args: any[]) => {
      if (!debug) return;
      console.log("[chat-sse]", ...args);
    },
    [debug],
  );

  const setStatusSafe = useCallback((s: SseStatus) => {
    statusRef.current = s;
    setStatus(s);
  }, []);

  const clearTimers = useCallback(() => {
    if (reconnectTimerRef.current != null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  /**
   * 解析 SSE 文本流，提取 event 和 data 字段
   */
  function parseSSEChunk(chunkText: string): Array<{ event: string; data: string }> {
    const results: Array<{ event: string; data: string }> = [];
    const blocks = chunkText.split("\n\n");

    for (const block of blocks) {
      const trimmed = block.trim();
      if (!trimmed) continue;

      let eventType = "message";
      let dataStr = "";

      for (const line of trimmed.split("\n")) {
        if (line.startsWith("event:")) {
          eventType = line.slice(6).trim();
        } else if (line.startsWith("data:")) {
          dataStr += line.slice(5).trim();
        }
      }

      if (dataStr) {
        results.push({ event: eventType, data: dataStr });
      }
    }

    return results;
  }

  /** 构建请求 URL（SSE 接口不带 /api 前缀） */
  function buildRequestUrl(): string {
    if (url.startsWith("http")) return url;

    const apiBase = import.meta.env.VITE_API_BASE_URL || "";

    if (!apiBase || apiBase.startsWith("/")) {
      return url.startsWith("/") ? url : "/" + url;
    }

    const sseBase = apiBase.replace(/\/api\/?$/, "");
    return `${sseBase}${url.startsWith("/") ? url : "/" + url}`;
  }

  /**
   * ★ 核心：建立 SSE 连接
   * 使用模块级 _globalGen 确保同一时刻全局只有 1 个活跃连接
   */
  const connect = useCallback(async () => {
    if (!enabled) return;
    if (!url) return;

    if (!userStoreRehydrated.current) {
      log("waiting for store rehydration...");
      return;
    }

    // ── Step 1: 立即递增 globalGen，使所有旧连接失效 ──
    const myGen = ++_globalGen;

    // ── Step 2: 立即中止任何旧的全局连接 ──
    _abortGlobal();

    // ── Step 3: 同步设置状态（不等 React 批量更新）──
    statusRef.current = "connecting";
    setStatus("connecting");
    closedByUserRef.current = false;
    clearTimers();
    setLastError(null);
    reconnectAttemptRef.current = 0;

    try {
      // 创建新的 controller 并注册到全局
      const controller = new AbortController();
      _activeAbortCtrl = controller;

      // ★ 关键：再次检查是否还是最新代（防止极短时间内的竞态）
      if (_globalGen !== myGen) {
        log("connect superseded before fetch");
        return;
      }

      const reqUrl = buildRequestUrl();
      log("connecting...", { url: reqUrl });

      // ★ localStorage 兜底：Zustand persist 可能还未异步恢复完毕
      let effectiveToken = token;
      if (!effectiveToken) {
        try {
          const raw = localStorage.getItem("user-storage");
          if (raw) { const p = JSON.parse(raw); effectiveToken = p?.state?.token ?? p?.token ?? null; }
        } catch {}
      }
      let effectiveSession = session_id;
      if (!effectiveSession) {
        try {
          const raw = localStorage.getItem("chat-session-storage");
          if (raw) { const p = JSON.parse(raw); effectiveSession = p?.state?.session_id ?? p?.session_id ?? null; }
        } catch {}
      }

      const response = await fetch(reqUrl, {
        method: "GET",
        headers: {
          Accept: "text/event-stream",
          ...(effectiveToken ? { "Chat-Token": effectiveToken } : {}),
          ...(effectiveSession ? { "Chat-Session": effectiveSession } : {}),
        },
        signal: controller.signal,
      });

      // ★ fetch 返回后再次检查
      if (_globalGen !== myGen || controller.signal.aborted) {
        log("fetch completed but superseded");
        try { response.body?.getReader().releaseLock(); } catch {}
        return;
      }

      if (!response.ok) {
        throw new Error(`SSE HTTP ${response.status}: ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error("Response body is not available");
      }

      setStatusSafe("open");
      log("connected");

      // ── 开始读取流 ──
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        // 每次循环都检查是否已被取代或中止
        if (_globalGen !== myGen || controller.signal.aborted) break;

        const { done, value } = await reader.read();
        if (done) {
          log("stream ended (done)");
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // 尝试解析完整的 SSE 事件（以 \n\n 结尾）
        const newlineIdx = buffer.lastIndexOf("\n\n");
        if (newlineIdx === -1) continue;

        const complete = buffer.substring(0, newlineIdx + 2);
        buffer = buffer.substring(newlineIdx + 2);

        // 再次检查（解析过程中可能被新 connect 取代）
        if (_globalGen !== myGen) break;

        const events = parseSSEChunk(complete);
        for (const ev of events) {
          if (_globalGen !== myGen) break;

          let parsedData: unknown;
          try {
            parsedData = JSON.parse(ev.data);
          } catch {
            parsedData = { raw: ev.data };
          }

          const sseMsg: ChatSseInbound = { event: ev.event, data: parsedData as any };

          // ── 处理 connected 事件 ──
          if (ev.event === "connected") {
            const identity = (parsedData as any)?.identity;
            if (identity) {
              if (typeof identity.uid === "number")
                setConnectedUid(identity.uid);
              // ★ 已登录用户 session_id 可能为 null，此时仍需记录 uid/is_guest 信息
              const sid = typeof identity.session_id === "string" ? identity.session_id : "";
              if (typeof identity.is_guest === "boolean") {
                useChatSessionStore.getState().setSession({
                  is_guest: identity.is_guest,
                  session_id: sid,
                  uid: identity.uid ?? 0,
                });
              }
            }
          }

          setLastMessage(sseMsg);
          onMessage?.(sseMsg);
        }
      }

      // 流正常结束 → 清理 reader
      try { reader.releaseLock(); } catch {}

      // 非主动关闭/非被取代 → 触发重连
      if (_globalGen === myGen && !controller.signal.aborted && !closedByUserRef.current) {
        setStatusSafe("closed");
        scheduleReconnect("stream_end");
      }
    } catch (error: any) {
      if (error.name === "AbortError" || error.message?.includes("aborted") || _globalGen !== myGen) {
        log("connection aborted/superseded");
        return;
      }

      log("connection error", error?.message || error);
      setLastError(error?.message || "SSE 连接错误");
      setStatusSafe("error");

      // 只有仍是最新一代才重连（避免旧连接的错误触发重连）
      if (_globalGen === myGen) {
        scheduleReconnect("exception");
      }
    }
  }, [
    enabled, url, token, session_id, onMessage,
    clearTimers, log, setStatusSafe,
  ]);

  const scheduleReconnect = useCallback(
    (reason: string) => {
      if (!enabled) return;
      if (closedByUserRef.current) return;
      if (!navigator.onLine) return;
      if (!token) return;

      const attempt = reconnectAttemptRef.current++;
      const base = reconnectMinDelayMs * Math.pow(2, attempt);
      const delay = jitter(clamp(base, reconnectMinDelayMs, reconnectMaxDelayMs));
      log("schedule reconnect", { reason, attempt, delay });

      clearTimers();
      reconnectTimerRef.current = setTimeout(() => {
        void connect();
      }, delay);
    },
    [enabled, reconnectMaxDelayMs, reconnectMinDelayMs, token, connect, log, clearTimers],
  );

  const close = useCallback(() => {
    closedByUserRef.current = true;
    clearTimers();
    reconnectAttemptRef.current = 0;
    setConnectedUid(null);
    // ★ 使所有进行中的连接全部失效
    _invalidate();
    setStatusSafe("closed");
  }, [clearTimers, setStatusSafe]);

  // ── 主 effect：管理连接生命周期 ──
  useEffect(() => {
    log("useEffect triggered", {
      enabled,
      url: url?.slice(0, 30),
      tokenChanged: !!token,
    });

    if (!userStoreRehydrated.current) {
      log("store not rehydrated, skip");
      return;
    }

    if (!enabled) {
      close();
      return;
    }

    void connect();

    return () => {
      log("cleanup → close");
      close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, url, token]);

  // ── 网络恢复时重连 ──
  useEffect(() => {
    const onOnline = () => {
      if (statusRef.current === "open") return;
      scheduleReconnect("online");
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [scheduleReconnect]);

  return {
    status,
    lastMessage,
    lastError,
    connectedUid,
    connect,
    close,
  };
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function jitter(ms: number) {
  const f = 0.85 + Math.random() * 0.3;
  return Math.round(ms * f);
}
